import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { isRequestActive, loadAndTrack } from "redux-request-loading";

import * as taskSelectors from "../../task/selectors";
import { ApplicationState } from "../../applicationState";

import * as selectors from "./selectors";
import { OhCaseDetailModel, OhCaseInRedux } from "./model";
import { loadOhCaseDetailSuccess } from "./actions";
import { ohCaseApi } from "./ohCaseApi";
import { LOAD_OHCASE_DETAIL } from "./actiontypes";

type useOhCaseDetailsModelReturn =
    { ohCase: OhCaseDetailModel; isLoading: false; isError: false; refresh: () => void; isLoaded: true }
    | { ohCase: Exclude<OhCaseInRedux, OhCaseDetailModel> | undefined; isLoading: boolean; isError: boolean; refresh: () => void; isLoaded: false };

type useOhCaseDetailsModelArguments =
    { ohCaseId: string; customerId: string; taskId?: undefined }
    | { taskId: string; ohCaseId?: undefined; customerId?: undefined };

/*
* This hook is used to load the ohCase details from the server using either the combination of customerId and ohCaseId
* or task id to load the case from an actionable task.
* Usually these args can be passed in from the route selectors.
* If you don't need the ohCase details, you can pass `enable: false` to the hook to disable the loading of the ohCase details
* until you need it by calling `refresh()`
*/
export const useOhCaseDetailsModel = (args: useOhCaseDetailsModelArguments, { enable }: { enable?: boolean } = {}): useOhCaseDetailsModelReturn => {
    if (args.ohCaseId === undefined && args.customerId === undefined && args.taskId === undefined) {
        throw new Error("useOhCaseDetailsModel can only be used with either both `ohCaseId` and `customerId` params "
        + "or with a `taskId` param.");
    }
    const actionableTasks = useSelector(taskSelectors.actionableTasksSelector);
    const task = actionableTasks.find(t => t.id === args.taskId);
    if (args.taskId !== undefined && task === undefined) {
        throw new Error("When using useOhCaseDetailsModel hook with taskId parameter it " +
            "currently requires that the task is in the actionable tasks part of the store.");
    }
    const dispatch = useDispatch();
    const customerId = args.customerId ?? task!.customer.id;
    const ohCaseId = args.ohCaseId ?? task!.caseId;
    const ohCases = useSelector(selectors.ohCasesSelector);
    const ohCase = ohCases.find(c => c.id === ohCaseId) as OhCaseInRedux | undefined;
    const requestKey = `${LOAD_OHCASE_DETAIL}_${customerId}_${ohCaseId}`;
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    const [isError, setIsError] = React.useState(false);
    const refresh = React.useCallback(async () => {
        try {
            // TODO: Replace this with Redux Toolkit
            // @ts-expect-error TS2345
            await loadAndTrack(dispatch, requestKey, (async () => {
                const result = await ohCaseApi.detail(customerId, ohCaseId);
                dispatch(loadOhCaseDetailSuccess(result));
            })());
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [customerId, dispatch, ohCaseId, requestKey]);
    React.useEffect(() => {
        if (enable === false
            || isLoading
            || isError
            || (ohCase && "detailUpdated" in ohCase && ohCase.detailUpdated.isSameOrAfter(ohCase.listUpdated))) {
            return;
        }

        refresh();
    }, [enable, isError, isLoading, ohCase, refresh]);

    if (ohCase && "detailUpdated" in ohCase) {
        return { ohCase, isLoading: false, isError: false, refresh, isLoaded: true };
    } else {
        return { ohCase, isLoading, isError, refresh, isLoaded: false };
    }
};
